import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useLinkSiteAddressToProject from '@/api/mutations/project/useLinkSiteAddressToProject';
import complianceKeys from '@/api/queries/compliance/keyFactory';
import projectKeys from '@/api/queries/project/keyFactory';
import {
  LinkSiteAddressToProjectInput,
  LinkSiteAddressToProjectMutation,
  ProjectByIdQuery,
  UnlinkSiteAddressFromProjectInput,
} from '@/gql/graphql';

const useOptimisticLinkSiteAddressToProject = (
  projectId: string,
  options?: UseMutationOptions<
    LinkSiteAddressToProjectMutation,
    unknown,
    {
      unlinkSiteAddressFromProjectInput?: UnlinkSiteAddressFromProjectInput;
      linkSiteAddressToProjectInput: LinkSiteAddressToProjectInput;
    }
  >
) => {
  const queryClient = useQueryClient();

  return useLinkSiteAddressToProject({
    ...options,
    onMutate: async (variables) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: projectKeys.overview(projectId),
      });

      // Snapshot the previous value
      const previousProjectOverview = queryClient.getQueryData(
        projectKeys.overview(projectId)
      );

      // Optimistically update to the new value
      // Note: We no longer have customer.addresses in the query, so we create a placeholder
      // that will be replaced when the query refetches after the mutation completes
      queryClient.setQueryData<ProjectByIdQuery>(
        projectKeys.overview(projectId),
        (old) => {
          if (!old) return;

          return {
            ...old,
            projectById: {
              ...old.projectById,
              addresses: {
                ...old.projectById.addresses,
                site: [
                  {
                    id:
                      variables.linkSiteAddressToProjectInput?.addressId || '',
                    publicId: 'temp-public-id',
                    line1: null,
                    line2: null,
                    city: null,
                    country: null,
                    postalCode: null,
                    fullLocalAddress: 'Loading...',
                    contacts: [],
                    energyPerformanceCertificates: [],
                  },
                ],
              },
            },
          };
        }
      );

      options?.onMutate?.(variables);

      // Return a context object with the snapshotted value
      return { previousProjectOverview };
    },
    onError: (err, newTodo, context) => {
      // If an error happens, rollback!
      queryClient.setQueryData(
        projectKeys.overview(projectId),
        // @ts-expect-error
        context.previousProjectOverview
      );

      options?.onError?.(err, newTodo, context);
    },
    // Always refetch after error or success:
    onSettled: (...args) => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.overview(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: complianceKeys.formPrefill(projectId),
      });
      // The site address drives the EPC-derived heat loss estimate, which in
      // turn determines whether existing Estimates are out of date.
      void queryClient.invalidateQueries({
        queryKey: projectKeys.heatLossEstimate(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: projectKeys.estimates(projectId),
      });

      options?.onSettled?.(...args);
    },
  });
};

export default useOptimisticLinkSiteAddressToProject;
